import { useMemo } from "react";
import React from "react";
import { twMerge } from "tailwind-merge";
import { MDXProvider, useMDXComponents } from "@mdx-js/react";
import type { MDXComponents } from "mdx/types";
import { evaluateSync } from "@mdx-js/mdx";
import * as runtime from "react/jsx-runtime";
import remarkGfm from "remark-gfm";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";
import { Checkbox } from "@themes/pollinator/ui/Checkbox";
import { createSlug } from "@core/utils/createSlug";
import { SupportArticleButton } from "./SupportArticleButton";

export type Props = {
  /** MDX content string to render */
  content: string;
  /** Additional CSS classes */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
};

type ComponentBaseProps = {
  children?: React.ReactNode;
  [key: string]: unknown;
};

const components = {
  h1: ({ children, ...props }: ComponentBaseProps) => {
    const id = createSlug(children?.toString() || "");
    return (
      <Type
        {...props}
        id={id}
        as="h1"
        variant="h3"
        className="scroll-mt-20 mt-12 pt-8 mb-8 border-t-1 border-brand-600 first:mt-0 first:border-t-0 first:pt-0"
      >
        {children}
      </Type>
    );
  },
  h2: ({ children, ...props }: ComponentBaseProps) => {
    const id = createSlug(children?.toString() || "");
    return (
      <Type
        {...props}
        id={id}
        as="h2"
        variant="h4"
        className="scroll-mt-20 pt-4 mt-0 mb-2 first:mt-0 first:pt-0 h2+[&]:mt-0"
      >
        {children}
      </Type>
    );
  },
  h3: ({ children, ...props }: ComponentBaseProps) => {
    const id = createSlug(children?.toString() || "");
    return (
      <Type
        {...props}
        id={id}
        as="h3"
        variant="h5"
        className="scroll-mt-20 mb-1 mt-6"
      >
        {children}
      </Type>
    );
  },
  p: ({ children, ...props }: ComponentBaseProps) => {
    return (
      <Type
        {...props}
        variant="body2"
        className={twMerge("w-full max-w-full mb-4")}
      >
        {children}
      </Type>
    );
  },
  blockquote: ({ children, ...props }: ComponentBaseProps) => (
    <blockquote
      {...props}
      className="border-l-4 border-brand-600 pl-6 text-brand-700 italic"
    >
      {children}
    </blockquote>
  ),
  img: ({ src, alt, ...props }: ComponentBaseProps) => (
    <img
      src={src as string}
      alt={alt as string}
      {...props}
      className="max-w-sm my-8 max-h-72 object-contain"
    />
  ),
  strong: ({ children, ...props }: ComponentBaseProps) => (
    <strong {...props} className="font-semibold">
      {children}
    </strong>
  ),
  a: ({ children, href, ...props }: any) => (
    <a {...props} href={href} className="underline hover:text-brand-600">
      {children}
    </a>
  ),
  button: ({ children, ...props }: ComponentBaseProps) => (
    <Button {...props}>{children}</Button>
  ),
  ul: ({ children, ...props }: ComponentBaseProps) => (
    <ul
      {...props}
      className="list-disc list-inside mb-4 flex flex-col gap-2"
    >
      {children}
    </ul>
  ),
  ol: ({ children, ...props }: ComponentBaseProps) => (
    <ol
      {...props}
      className="list-decimal list-inside mb-4 flex flex-col gap-2"
    >
      {children}
    </ol>
  ),
  li: ({ children, ...props }: ComponentBaseProps) => (
    <li
      {...props}
      className="pl-6 -indent-6 [&>p]:inline [&>p]:m-0 [&>button]:inline-flex [&>button]:align-baseline"
    >
      {children}
    </li>
  ),
  hr: ({ ...props }: ComponentBaseProps) => (
    <hr {...props} className="my-4 border-transparent" />
  ),
  input: ({ ...props }: ComponentBaseProps) => {
    if (props.type === "checkbox") {
      return (
        <Checkbox
          {...props}
          className="border-none shadow-none inline-flex w-auto p-0 pr-4 mt-1"
        />
      );
    }
    return <input {...props} />;
  },
  Button,
  Type,
  SupportArticleButton,
};

/**
 * MDX content renderer with custom component mappings.
 */
export function MdxRenderer({ content, className, style = {} }: Props) {
  const MDXContent = useMemo(() => {
    try {
      const result = evaluateSync(content, {
        ...runtime,
        remarkPlugins: [remarkGfm],
        scope: {
          Button,
          Type,
          SupportArticleButton,
        },
      } as Parameters<typeof evaluateSync>[1]);
      return result.default;
    } catch (err) {
      console.error("MDX compilation error:", err);
      return null;
    }
  }, [content]);

  const patchedComponents = useMDXComponents(components as unknown as MDXComponents);

  if (!MDXContent) {
    return (
      <div className="text-red-600" data-testid="mdx-renderer-error">
        Error rendering content
      </div>
    );
  }

  return (
    <div className={twMerge(className)} style={style} data-testid="mdx-renderer">
      <MDXProvider components={patchedComponents}>
        <MDXContent components={patchedComponents} />
      </MDXProvider>
    </div>
  );
}

export default MdxRenderer;

